import Prompt, { PromptStatus } from "@/models/prompt/prompt";
import { FC, useState } from "react";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@capacit-com/lara-law-shared-ui";
import { TagType } from "@/models/tag/tag";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Check, Edit2, Trash2, X } from "lucide-react";
import { ScrollArea } from "@capacit-com/lara-law-shared-ui";
import { Separator } from "@capacit-com/lara-law-shared-ui";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { Pill } from "@capacit-com/lara-law-shared-ui";
import usePrompts from "@/hooks/usePrompts";
import {
  showSuccessToast,
  showErrorToast,
} from "@/components/custom-toasts/toasts";

type ReviewPromptDialogContentProps = {
  prompt: Prompt;
  onEdit: () => void;
  handleClose: () => void;
};

const ReviewPromptDialogContent: FC<ReviewPromptDialogContentProps> = ({
  prompt,
  onEdit,
  handleClose,
}) => {
  const { remove, updateStatusAndInvalidate } = usePrompts();

  const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleAcceptAndClose = async (id: string) => {
    try {
      await updateStatusAndInvalidate.mutateAsync({
        id,
        status: PromptStatus.Accepted,
      });
      handleClose();
      showSuccessToast("Prompten er blevet accepteret");
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved accept af prompten: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  const handleRejectAndClose = async (id: string) => {
    try {
      await updateStatusAndInvalidate.mutateAsync({
        id,
        status: PromptStatus.Rejected,
      });
      handleClose();

      showSuccessToast("Prompten er blevet afvist");
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved afvisning af prompten: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  const handleDeleteAndClose = async (id: string) => {
    try {
      await remove.mutateAsync(id);
      handleClose();
      showSuccessToast("Prompten er blevet slettet");
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved sletning af prompten: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 flex-1 min-h-0">
        <DialogHeader className="flex-none text-2xl font-semibold">
          <DialogTitle>Review prompt: {prompt.title}</DialogTitle>
        </DialogHeader>

        <div className="py-4 flex-1 min-h-0 flex flex-col">
          <div className="space-y-4 flex-1 min-h-0 flex flex-col">
            <div className="grid grid-cols-4 gap-4 flex-none">
              <div className="col-span-1">
                <h3 className="text-sm font-medium mb-2">Afdeling</h3>
                <div className="flex flex-wrap gap-2">
                  {prompt?.tags
                    .filter((tag) => tag.tagType === TagType.Department)
                    .map((tag, idx) => (
                      <Pill pillLabel={tag.name} index={idx} />
                    ))}
                </div>
              </div>
              <div className="col-span-3">
                <h3 className="text-sm font-medium mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {prompt?.tags
                    .filter((tag) => tag.tagType === TagType.Tag)
                    .map((tag, idx) => (
                      <Pill pillLabel={tag.name} index={idx} />
                    ))}
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex-none">
              <h3 className="text-sm font-medium mb-2">Beskrivelse</h3>
              <div className="min-h-[10vh] rounded-md border">
                <ScrollArea className="h-full p-4">
                  <p className="text-sm">{prompt?.description}</p>
                </ScrollArea>
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
              <h3 className="text-sm font-medium mb-2">Prompt</h3>
              <div className="flex-1 min-h-20 rounded-md border">
                <ScrollArea className="h-full max-h-full p-4">
                  <p className="text-sm whitespace-pre-wrap">
                    {prompt?.promptString}
                  </p>
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-end">
          <Button variant="outline" onClick={onEdit} size={"sm"}>
            <Edit2 className="size-4 mr-2" />
            Rediger
          </Button>
          {prompt.status === PromptStatus.Accepted && (
            <Button
              variant="destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
              size={"sm"}
            >
              <Trash2 className="size-4 mr-2" />
              Slet
            </Button>
          )}
          {prompt.status === PromptStatus.Pending && (
            <>
              <Button
                variant="destructive"
                size={"sm"}
                onClick={() => setIsRejectDialogOpen(true)}
              >
                <X className="size-4 mr-2" />
                Afvis
              </Button>
              <Button
                variant="default"
                className="bg-success hover:bg-success/90"
                onClick={() => setIsAcceptDialogOpen(true)}
                size={"sm"}
              >
                <Check className="size-4 mr-2" />
                Accepter
              </Button>
            </>
          )}
        </DialogFooter>
      </div>
      <ConfirmationDialog
        isOpen={isAcceptDialogOpen}
        onClose={() => setIsAcceptDialogOpen(false)}
        onConfirm={() => handleAcceptAndClose(prompt.id)}
        title="Bekræft accept"
        description="Er du sikker på, at du vil acceptere denne prompt?"
        confirmText="Accepter"
        confirmIcon={<Check className="size-4 mr-2" />}
        variant="default"
        confirmPending={updateStatusAndInvalidate.isPending}
      />

      <ConfirmationDialog
        isOpen={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onConfirm={() => handleRejectAndClose(prompt.id)}
        title="Bekræft afvisning"
        description="Er du sikker på, at du vil afvise denne prompt?"
        confirmText="Afvis"
        confirmIcon={<X className="size-4 mr-2" />}
        variant="destructive"
        confirmPending={updateStatusAndInvalidate.isPending}
      />

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={async () => await handleDeleteAndClose(prompt.id)}
        title="Bekræft sletning"
        description="Er du sikker på, at du vil slette denne prompt? Denne handling kan ikke fortrydes."
        confirmText="Slet"
        confirmIcon={<Trash2 className="size-4 mr-2" />}
        variant="destructive"
        confirmPending={remove.isPending}
      />
    </>
  );
};

export default ReviewPromptDialogContent;
